import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Link, Tooltip, Typography } from '@mui/material';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import DoneAllOutlinedIcon from '@mui/icons-material/DoneAllOutlined';
import { brandColors } from '../../styles/brandColors';

export type DashboardGroupTheme = 'messages' | 'uploads';

const themeStyles = {
  messages: {
    groupBar: '#e8edf5',
    border: 'rgba(0, 30, 96, 0.09)',
    card: '#ffffff',
  },
  uploads: {
    groupBar: '#e7efe8',
    border: 'rgba(31, 77, 45, 0.14)',
    card: '#ffffff',
  },
};

const unstyledRouterLinkSx = {
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    textDecoration: 'none',
    color: 'inherit',
  },
  '&:visited': {
    color: 'inherit',
  },
};

export const formatDashboardDateTime = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const dateStr = date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
  const timeStr = date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toLowerCase();
  return `${dateStr} ${timeStr}`;
};

type FacilityRowProps = {
  facilityId: string | null | undefined;
  tenantName: string;
  sx?: object;
};

export const DashboardClaimFacilityRow: React.FC<FacilityRowProps> = ({
  facilityId,
  tenantName,
  sx,
}) => {
  const facilityLabel = facilityId?.trim() || '—';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'baseline',
        gap: 1,
        minWidth: 0,
        flexWrap: 'wrap',
        ...sx,
      }}
    >
      <Typography
        component="span"
        sx={{
          fontWeight: 700,
          fontSize: '1.05rem',
          color: brandColors.red,
          lineHeight: 1.2,
        }}
      >
        {facilityLabel}
      </Typography>
      <Typography
        component="span"
        sx={{
          color: '#64748b',
          fontSize: '0.8125rem',
          fontWeight: 500,
          lineHeight: 1.3,
        }}
      >
        {tenantName || 'Tenant'}
      </Typography>
    </Box>
  );
};

type TimestampProps = {
  timestamp: string;
  sx?: object;
};

export const DashboardNoteTimestamp: React.FC<TimestampProps> = ({ timestamp, sx }) => (
  <Box
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.35,
      color: '#475569',
      fontSize: '0.72rem',
      fontWeight: 600,
      flexShrink: 0,
      ...sx,
    }}
  >
    <ScheduleOutlinedIcon sx={{ fontSize: 13, color: '#64748b' }} />
    {formatDashboardDateTime(timestamp)}
  </Box>
);

type MetaRowProps = {
  facilityId: string | null | undefined;
  tenantName: string;
  timestamp: string;
  sx?: object;
};

export const DashboardClaimMetaRow: React.FC<MetaRowProps> = ({
  facilityId,
  tenantName,
  timestamp,
  sx,
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1,
      mb: 0.75,
      flexWrap: 'wrap',
      ...sx,
    }}
  >
    <DashboardClaimFacilityRow facilityId={facilityId} tenantName={tenantName} />
    <DashboardNoteTimestamp timestamp={timestamp} />
  </Box>
);

type Props = {
  claimId: number;
  claimNumber: string;
  facilityId: string | null | undefined;
  tenantName: string;
  statusLabel: string;
  timestamp: string;
  onClearAll: () => void;
  clearing: boolean;
  clearAllTooltip: string;
  theme: DashboardGroupTheme;
  hideMetaRow?: boolean;
  children: React.ReactNode;
};

const DashboardClaimGroupCard: React.FC<Props> = ({
  claimId,
  claimNumber,
  facilityId,
  tenantName,
  statusLabel,
  timestamp,
  onClearAll,
  clearing,
  clearAllTooltip,
  theme,
  hideMetaRow = false,
  children,
}) => {
  const colors = themeStyles[theme];
  const claimPath = `/claims/${claimId}`;

  return (
    <Box
      sx={{
        mb: 1,
        borderRadius: 1.5,
        overflow: 'hidden',
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.card,
        '&:last-child': { mb: 0 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.5,
          py: 0.75,
          backgroundColor: colors.groupBar,
          flexWrap: 'wrap',
        }}
      >
        <Box
          component={RouterLink}
          to={claimPath}
          aria-label={`Open claim ${claimNumber}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            minWidth: 0,
            flexWrap: 'wrap',
            flex: 1,
            ...unstyledRouterLinkSx,
            cursor: 'pointer',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 700, color: brandColors.navy }}>
            Claim ID: {claimNumber}
          </Typography>
          <OpenInNewOutlinedIcon sx={{ fontSize: 14, color: brandColors.navy }} />
          {statusLabel ? (
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1,
                py: 0.2,
                borderRadius: 999,
                backgroundColor: 'rgba(100, 116, 139, 0.14)',
                color: '#475569',
                fontSize: '0.7rem',
                fontWeight: 700,
                lineHeight: 1.4,
              }}
            >
              {statusLabel}
            </Box>
          ) : null}
        </Box>
        <Tooltip title={clearAllTooltip}>
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={(event) => {
              event.stopPropagation();
              event.preventDefault();
              onClearAll();
            }}
            disabled={clearing}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.35,
              color: brandColors.red,
              fontWeight: 600,
              fontSize: '0.75rem',
              border: 'none',
              background: 'none',
              cursor: clearing ? 'wait' : 'pointer',
              opacity: clearing ? 0.6 : 1,
              flexShrink: 0,
            }}
          >
            <DoneAllOutlinedIcon sx={{ fontSize: 14 }} />
            Clear All
          </Link>
        </Tooltip>
      </Box>

      <Box sx={{ px: 1.5, py: 1.25 }}>
        {!hideMetaRow ? (
          <DashboardClaimMetaRow
            facilityId={facilityId}
            tenantName={tenantName}
            timestamp={timestamp}
          />
        ) : null}
        {children}
      </Box>
    </Box>
  );
};

export default DashboardClaimGroupCard;
